import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  leadRetriesDelete,
  leadRetriesListGet,
  useLeadRetriesList
} from '../../../store/slices/tenantSlices/configurationSlice';
import { checkModulePermission } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import AddEditLeadRetriesModal from './AddEditLeadRetriesModal';

const LeadRetries = () => {
  const dispatch = useDispatch();

  const leadRetriesList = useLeadRetriesList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchLeadRetriesList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(leadRetriesListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteLeadRetries = async () => {
    try {
      const response = await dispatch(leadRetriesDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (leadRetriesList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchLeadRetriesList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchLeadRetriesList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Lead Retries"
        isExpandSearch
        isAdd
        isAddDisabled={!checkModulePermission('is_add', 'Lead Retries', userRolePermission)}
        onAddClick={() => setOpenAddModal(true)}
        addBtnName="Add Lead Retries"
        searchPlaceholder="Search Lead Retries"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : leadRetriesList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Lead Retries', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Process Name</th>
                  <th>Interval Type</th>
                  <th>Interval Time</th>
                  <th>Attempts</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {leadRetriesList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.process_name}</td>
                      <td className="text-capitalize">{val?.interval_type}</td>
                      <td>{val?.interval_time || '-'}</td>
                      <td>{val?.attempts}</td>
                      <td>{val?.status?.toString()}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Lead Retries', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_edit',
                                    'Lead Retries',
                                    userRolePermission
                                  )
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission(
                                'is_delete',
                                'Lead Retries',
                                userRolePermission
                              )
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    'Lead Retries',
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={leadRetriesList?.total}
              limit={leadRetriesList?.limit}
              page={leadRetriesList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchLeadRetriesList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteLeadRetries()}
      />
      <AddEditLeadRetriesModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchLeadRetriesList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default LeadRetries;
